import { PropsWithChildren, useCallback, useEffect, useMemo } from 'react';

import { SplashScreen, createCtx } from '@pletly/ui';
import { getAccessToken, getMediaToken, getUserFromToken } from '../../helpers/auth';
import { unauthenticatedClient } from '../../api/apollo';
import { eventEmitter } from '../../services/eventEmitter';
import { useAuthFunctions } from './useAuthFunctions';
import { AuthState } from './store/reducer';
import { useAuthState } from './store/useAuthState';
import { UserVisibility } from '../../../__generated__/globalTypes';
import { getCallbackUrl } from './store/callbackUrl';

const [useAuth, AuthDataProvider] = createCtx<ContextType>();

const AuthProvider = ({
	onLogin,
	beforeLogOut,
	children,
}: PropsWithChildren<{
	onLogin?: () => void | Promise<void>;
	beforeLogOut?: () => void | Promise<void>;
}>) => {
	const [state, dispatch] = useAuthState();
	const { login, abortLogin, register, abortRegister, logout, renewToken } = useAuthFunctions(
		dispatch,
		{ onLogin, beforeLogOut },
	);

	const authContext: ContextType = useMemo(
		() => ({
			state,
			login,
			abortLogin,
			logout,
			register,
			abortRegister,
			renewToken,
		}),
		[abortLogin, abortRegister, login, logout, register, renewToken, state],
	);
	const loadTokensAsync = useCallback(async () => {
		let accessToken;
		let mediaToken;

		try {
			accessToken = await getAccessToken(unauthenticatedClient);
			mediaToken = await getMediaToken();
		} catch (error) {
			console.error(error);
		}

		if (!accessToken || !mediaToken) {
			return dispatch({ type: 'FINISH_LOADING' });
		}

		const callbackUrl = await getCallbackUrl();
		const { userId, uuid, profile, features } = getUserFromToken(accessToken);
		const user = { ...profile, id: userId.toString(), uuid };

		dispatch({
			type: 'RESTORE_TOKEN',
			token: accessToken,
			mediaToken,
			user,
			features,
			callbackUrl,
		});
	}, [dispatch]);

	useEffect(() => {
		void loadTokensAsync();
	}, [loadTokensAsync]);

	useEffect(() => {
		const handleLogoutFromEvent = () => logout(false);

		eventEmitter.on('logout', logout);
		eventEmitter.on('logoutReceivedFromTab', handleLogoutFromEvent);

		return () => {
			eventEmitter.removeListener('logout', logout);
			eventEmitter.removeListener('logoutReceivedFromTab', handleLogoutFromEvent);
		};
	}, [logout]);

	useEffect(() => {
		eventEmitter.on('tokenRefreshed', loadTokensAsync);

		return () => {
			eventEmitter.removeListener('tokenRefreshed', loadTokensAsync);
		};
	}, [loadTokensAsync]);

	// 🚨 this is the important bit.
	// Normally your provider components render the context provider with a value.
	// But we post-pone rendering any of the children until after we've determined
	// whether or not we have a user token and if we do, then we render a spinner
	// while we go retrieve that user's information.
	if (state.isLoading) {
		return <SplashScreen />;
	}

	return <AuthDataProvider value={authContext}>{children}</AuthDataProvider>;
};

export { AuthProvider, useAuth };

type ContextType = {
	state: AuthState;
	login: (
		data: { email: string; password: string } | { username: string; password: string },
	) => Promise<void>;
	logout: () => Promise<void>;
	register: (data: {
		email: string;
		username: string;
		password: string;
		newsletter: boolean;
		registerCode?: string;
	}) => Promise<void>;
	renewToken: () => Promise<void>;
	abortLogin: () => void;
	abortRegister: () => void;
};

export type User = {
	id: string;
	uuid: string;
	email: string;
	username: string;
	photoUrl?: string;
	firstName?: string | null;
	lastName?: string | null;
	telephone?: string | null;
	info?: string | null;
	emailVerified: boolean;
	role: 'PAL' | 'USER' | 'ORGANISATION_ADMIN';
	restrictedAccess?: boolean | null;
	receiveSummaryNotificationEmails?: boolean | null;
	visibility: UserVisibility;
};
